import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  IconButton, Tooltip, Chip, MenuItem, Switch, FormControlLabel, Divider,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { planningAPI, configAPI, engineeringTasksAPI } from '../services/api';

const FALLBACK_DISCIPLINES = [
  'Proceso', 'Mecanica', 'Piping', 'Electrica',
  'Instrumentacion', 'Programacion', 'Civil', 'Puesta en marcha',
];

const EMPTY_FORM = { name: '', discipline: '', start_date: '', weeks: '', notes: '' };
const EMPTY_TASK_FORM = { assigned_to: '', status: 'pending', progress: '', document_ref: '', description: '' };

const TASK_STATUSES = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'in_progress', label: 'En curso' },
  { value: 'review', label: 'En revisión' },
  { value: 'completed', label: 'Completado' },
];

function formatDate(dt) {
  if (!dt) return '';
  try { return new Date(dt).toLocaleDateString('es-ES'); } catch { return ''; }
}

function toDateInput(dt) {
  if (!dt) return '';
  try { return new Date(dt).toISOString().slice(0, 10); } catch { return ''; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Gantt chart component  (drag to move · resize handle to extend)
// ─────────────────────────────────────────────────────────────────────────────
function GanttChart({ lines, onUpdate }) {
  const CELL_W = 36;
  const LABEL_W = 200;
  const MS_PER_WEEK = 7 * 24 * 3600 * 1000;

  // ── Drag state (ref = no stale closure in listeners; state = trigger re-render) ──
  const dragRef  = useRef(null);          // { lineId, type, startX, origStartWeek, origDuration, deltaWeeks }
  const [dragState, setDragState] = useState(null); // { lineId, type, deltaWeeks }

  // Compute base data (always, even if linesWithDates empty – hooks must be before returns)
  const linesWithDates = lines.filter((l) => l.start_date && l.weeks);
  const startDates = linesWithDates.map((l) => new Date(l.start_date).getTime());
  const minTs   = linesWithDates.length ? Math.min(...startDates) : Date.now();
  const minDate = new Date(minTs);

  const enriched = linesWithDates.map((l) => {
    const startWeek    = (new Date(l.start_date).getTime() - minTs) / MS_PER_WEEK;
    const durationWeeks = parseFloat(l.weeks) || 1;
    return { ...l, startWeek, durationWeeks };
  });

  // Extend totalWeeks to encompass drag preview
  let maxWeek = enriched.length ? Math.max(...enriched.map((l) => l.startWeek + l.durationWeeks)) : 8;
  if (dragState) {
    const dl = enriched.find((l) => l.id === dragState.lineId);
    if (dl) {
      const end = dragState.type === 'move'
        ? Math.max(0, dl.startWeek + dragState.deltaWeeks) + dl.durationWeeks
        : dl.startWeek + Math.max(0.5, dl.durationWeeks + dragState.deltaWeeks);
      maxWeek = Math.max(maxWeek, end);
    }
  }
  const totalWeeks = Math.min(Math.ceil(maxWeek) + 2, 104);
  const weeks = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  // ── Mouse event listeners (attached to window while a drag is active) ──
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const deltaWeeks = dx / CELL_W;
      dragRef.current.deltaWeeks = deltaWeeks;           // keep ref fresh
      setDragState((prev) => prev ? { ...prev, deltaWeeks } : null);
    };

    const onMouseUp = () => {
      if (!dragRef.current) return;
      const { lineId, type, origStartWeek, origDuration, deltaWeeks } = dragRef.current;
      dragRef.current = null;
      setDragState(null);

      if (!onUpdate) return;
      const snapped = Math.round(deltaWeeks);
      if (snapped === 0) return;                        // nothing changed

      if (type === 'move') {
        const newStartWeek = Math.max(0, origStartWeek + snapped);
        const newTs        = minTs + newStartWeek * MS_PER_WEEK;
        const newDate      = new Date(newTs).toISOString().slice(0, 10);
        onUpdate(lineId, { start_date: `${newDate}T00:00:00`, weeks: origDuration });
      } else {
        const newDuration  = Math.max(0.5, origDuration + snapped);
        const origTs       = minTs + origStartWeek * MS_PER_WEEK;
        const origDate     = new Date(origTs).toISOString().slice(0, 10);
        onUpdate(lineId, { start_date: `${origDate}T00:00:00`, weeks: newDuration });
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, [minTs, onUpdate]);

  const handleMouseDown = (e, line, type) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      lineId: line.id,
      type,
      startX: e.clientX,
      origStartWeek:  line.startWeek,
      origDuration:   line.durationWeeks,
      deltaWeeks:     0,
    };
    setDragState({ lineId: line.id, type, deltaWeeks: 0 });
  };

  if (!linesWithDates.length) return null;

  return (
    <Box sx={{ mt: 3, overflowX: 'auto', borderTop: '1px solid #e0e0e0', pt: 2, userSelect: 'none' }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        Diagrama de Gantt — semanas desde {minDate.toLocaleDateString('es-ES')}
      </Typography>
      <Box sx={{ minWidth: LABEL_W + totalWeeks * CELL_W }}>
        {/* Header */}
        <Box sx={{ display: 'flex', borderBottom: '2px solid #bdbdbd', backgroundColor: '#fafafa' }}>
          <Box sx={{ width: LABEL_W, flexShrink: 0, py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>
            Tarea
          </Box>
          {weeks.map((w) => (
            <Box
              key={w}
              sx={{
                width: CELL_W, flexShrink: 0, textAlign: 'center',
                fontSize: '0.6rem', color: 'text.secondary',
                borderLeft: w % 4 === 1 ? '1px solid #bdbdbd' : '1px solid #f0f0f0',
                py: 0.5,
              }}
            >
              {w % 2 === 0 ? '' : `S${w}`}
            </Box>
          ))}
        </Box>

        {/* Rows */}
        {enriched.map((line) => {
          // Live preview during drag
          let dispStart    = line.startWeek;
          let dispDuration = line.durationWeeks;
          if (dragState && dragState.lineId === line.id) {
            if (dragState.type === 'move') {
              dispStart = Math.max(0, line.startWeek + dragState.deltaWeeks);
            } else {
              dispDuration = Math.max(0.5, line.durationWeeks + dragState.deltaWeeks);
            }
          }
          const barLeft  = dispStart    * CELL_W;
          const barWidth = Math.max(dispDuration * CELL_W, 6);
          const isDragging = dragState && dragState.lineId === line.id;

          return (
          <Box
            key={line.id}
            sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0', '&:hover': { backgroundColor: '#f9f9f9' } }}
          >
            <Box
              sx={{
                width: LABEL_W, flexShrink: 0, fontSize: '0.72rem',
                px: 1, py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {line.name}
            </Box>
            {/* Week grid + bar */}
            <Box sx={{ position: 'relative', width: totalWeeks * CELL_W, height: 28, flexShrink: 0 }}>
              {/* Grid lines */}
              {weeks.map((w) => (
                <Box
                  key={w}
                  sx={{
                    position: 'absolute',
                    left: (w - 1) * CELL_W,
                    top: 0, bottom: 0,
                    width: CELL_W,
                    borderLeft: w % 4 === 1 ? '1px solid #ddd' : '1px solid #f5f5f5',
                  }}
                />
              ))}
              {/* Bar (drag to move) */}
              <Box
                onMouseDown={(e) => handleMouseDown(e, line, 'move')}
                sx={{
                  position: 'absolute',
                  left: barLeft,
                  width: barWidth,
                  height: 18,
                  top: 5,
                  backgroundColor: line.task_id ? '#1976d2' : '#ff9800',
                  borderRadius: 1,
                  opacity: isDragging ? 0.95 : 0.85,
                  cursor: 'grab',
                  transition: isDragging ? 'none' : 'left 0.05s, width 0.05s',
                  boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                  display: 'flex', alignItems: 'center',
                }}
                title={`${line.name} — ${dispDuration.toFixed(1)} sem · Arrastrar para mover, borde dcho para redimensionar`}
              >
                {/* Resize handle – right edge */}
                <Box
                  onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, line, 'resize'); }}
                  sx={{
                    position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
                    cursor: 'ew-resize',
                    backgroundColor: 'rgba(0,0,0,0.18)',
                    borderRadius: '0 4px 4px 0',
                  }}
                />
              </Box>
            </Box>
          </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Azul = Ing. Detalle &nbsp;·&nbsp; Naranja = manual &nbsp;·&nbsp;
        <strong>Arrastrar</strong> barra para mover &nbsp;·&nbsp;
        <strong>Borde derecho</strong> para cambiar duración
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function ProjectPlanning({ projectId }) {
  const [lines, setLines] = useState([]);
  const [disciplines, setDisciplines] = useState(FALLBACK_DISCIPLINES);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [addAsTask, setAddAsTask] = useState(false);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const loadLines = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const resp = await planningAPI.list(projectId);
      setLines(resp.data);
      setError(null);
    } catch {
      setError('Error al cargar el planning.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleGanttUpdate = useCallback(async (lineId, changes) => {
    try {
      await planningAPI.update(lineId, changes);
      await loadLines();
    } catch (err) {
      setError('Error al actualizar desde el Gantt.');
    }
  }, [loadLines]);

  useEffect(() => { loadLines(); }, [loadLines]);

  // Load disciplines from config
  useEffect(() => {
    configAPI.listDisciplines()
      .then((r) => { if (r.data.length) setDisciplines(r.data.map((d) => d.name)); })
      .catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const resp = await planningAPI.sync(projectId);
      setLines(resp.data);
      setError(null);
    } catch {
      setError('Error al sincronizar desde tareas de ingeniería.');
    } finally {
      setSyncing(false);
    }
  };

  const openCreate = () => { setForm(EMPTY_FORM); setTaskForm(EMPTY_TASK_FORM); setAddAsTask(false); setEditing(null); setDlgOpen(true); };
  const openEdit = (line) => {
    setForm({
      name: line.name || '',
      discipline: line.discipline || '',
      start_date: toDateInput(line.start_date),
      weeks: line.weeks ?? '',
      notes: line.notes || '',
    });
    setEditing(line);
    setDlgOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        discipline: form.discipline || null,
        start_date: form.start_date ? `${form.start_date}T00:00:00` : null,
        weeks: form.weeks !== '' ? parseFloat(form.weeks) : null,
        notes: form.notes || null,
      };
      if (editing) {
        await planningAPI.update(editing.id, payload);
      } else if (addAsTask) {
        // Create engineering task first, then link planning line to it
        const taskPayload = {
          name: form.name,
          discipline: form.discipline || null,
          start_date: form.start_date ? `${form.start_date}T00:00:00` : null,
          weeks: form.weeks !== '' ? parseFloat(form.weeks) : null,
          notes: form.notes || null,
          assigned_to: taskForm.assigned_to || null,
          status: taskForm.status || 'pending',
          progress: taskForm.progress !== '' ? parseFloat(taskForm.progress) : null,
          document_ref: taskForm.document_ref || null,
          description: taskForm.description || null,
        };
        const taskResp = await engineeringTasksAPI.create(projectId, taskPayload);
        // Planning line linked to the new task
        await planningAPI.create(projectId, { ...payload, task_id: taskResp.data.id });
      } else {
        await planningAPI.create(projectId, payload);
      }
      await loadLines();
      setDlgOpen(false);
    } catch {
      setError('Error al guardar la línea de planning.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await planningAPI.delete(id);
      setLines((prev) => prev.filter((l) => l.id !== id));
    } catch {
      setError('Error al eliminar la línea.');
    }
  };

  if (!projectId) return <Alert severity="warning">No se ha seleccionado un proyecto.</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>Planning del Proyecto</Typography>
          <Typography variant="body2" color="text.secondary">
            Planificación temporal del proyecto. Las líneas sincronizadas provienen de las tareas definidas en
            Ingeniería de Detalle (con fecha de inicio y semanas). Las líneas manuales se mantienen al sincronizar.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sincronizar tareas
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Añadir línea
          </Button>
        </Box>
      </Box>

      {lines.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc' }}>
          <Typography color="text.secondary">
            No hay líneas de planning. Pulsa "Sincronizar tareas" para importar las tareas con fecha y duración
            definidas en Ingeniería de Detalle, o "Añadir línea" para crear una línea manualmente.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#c62828' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Tarea</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Disciplina</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>F. Inicio</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: '#fff' }}>Semanas</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>F. Fin (est.)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Notas</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#fff' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => {
                  const endDate = line.start_date && line.weeks
                    ? new Date(new Date(line.start_date).getTime() + parseFloat(line.weeks) * 7 * 24 * 3600 * 1000)
                    : null;
                  return (
                    <TableRow key={line.id} hover>
                      <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {line.name}
                      </TableCell>
                      <TableCell>{line.discipline || '—'}</TableCell>
                      <TableCell>{formatDate(line.start_date) || '—'}</TableCell>
                      <TableCell align="center">{line.weeks ?? '—'}</TableCell>
                      <TableCell>{endDate ? endDate.toLocaleDateString('es-ES') : '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={line.task_id ? 'Ing. Detalle' : 'Manual'}
                          size="small"
                          color={line.task_id ? 'primary' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                        {line.notes || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(line)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(line.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, pb: 2 }}>
            <GanttChart lines={lines} onUpdate={handleGanttUpdate} />
          </Box>
        </Paper>
      )}

      {/* Edit/Create dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar línea de planning' : 'Nueva línea de planning'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* ── Planning fields ── */}
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Nombre / Descripción de la tarea"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth select label="Disciplina"
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
              >
                <MenuItem value="">— Sin disciplina —</MenuItem>
                {disciplines.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth label="Fecha de inicio" type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth label="Duración (semanas)" type="number"
                value={form.weeks}
                onChange={(e) => setForm({ ...form, weeks: e.target.value })}
                inputProps={{ min: 0.5, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Notas"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>

            {/* ── Switch: also create as engineering task ── */}
            {!editing && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={addAsTask}
                      onChange={(e) => setAddAsTask(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">Añadir también como tarea en Ing. Detalle</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Crea la tarea en Ingeniería de Detalle y la vincula a esta línea de planning
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            )}

            {/* ── Extra task fields (only when switch is on) ── */}
            {!editing && addAsTask && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 0.5, mb: -0.5 }}>
                    Datos adicionales de la tarea (Ing. Detalle)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Responsable"
                    value={taskForm.assigned_to}
                    onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth select label="Estado"
                    value={taskForm.status}
                    onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                  >
                    {TASK_STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth label="Avance (%)" type="number"
                    value={taskForm.progress}
                    onChange={(e) => setTaskForm({ ...taskForm, progress: e.target.value })}
                    inputProps={{ min: 0, max: 100, step: 5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Referencia de documento"
                    value={taskForm.document_ref}
                    onChange={(e) => setTaskForm({ ...taskForm, document_ref: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth multiline rows={2} label="Descripción detallada"
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Guardando...' : (addAsTask && !editing ? 'Crear en Planning + Tareas' : 'Guardar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="xs">
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <Typography>¿Estas seguro de que deseas eliminar esta linea de planificacion?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={async () => { await handleDelete(deleteConfirmId); setDeleteConfirmId(null); }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
